/**
 * @file date-picker/use-date-range-picker-state.ts
 *
 * Core state hook for DateRangePicker — hoisted state pattern.
 * Equivalent to `rememberDateRangePickerState()` in Android Compose Material3.
 *
 * Range selection logic:
 * - Click 1: set start date
 * - Click 2 (ms > start): set end date → range complete
 * - Click 2 (ms <= start): reset, set start = ms
 * - If both start and end exist: reset, set start = ms
 *
 * @example
 * ```tsx
 * const state = useDateRangePickerState();
 *
 * <DateRangePicker state={state} />
 * <p>Start: {state.selectedStartMs}, End: {state.selectedEndMs}</p>
 * ```
 */

import { useCallback, useMemo, useReducer, useState } from "react";
import {
	getNavigableMonth,
	isMonthNavigable,
	normalizeToMonthStart,
	resolveLocale,
	startOfDayUTC,
	todayUTC,
} from "../shared/calendar-model";
import type { DisplayMode } from "../shared/types";
import { ALL_DATES_SELECTABLE } from "../shared/types";
import type {
	DateRangePickerState,
	UseDateRangePickerStateOptions,
} from "./date-picker.types";

// ─── Defaults ─────────────────────────────────────────────────────────────────

const DEFAULT_YEAR_RANGE: [number, number] = [1900, 2100];

// ─── Range Reducer ────────────────────────────────────────────────────────────

interface RangeState {
	startMs: number | null;
	endMs: number | null;
}

type RangeAction =
	| { type: "SELECT"; ms: number }
	| { type: "CLEAR" }
	| { type: "RESET"; startMs: number | null; endMs: number | null };

/**
 * Pure reducer to update start/end atomically in a single dispatch —
 * avoids nested setState anti-pattern and stale closure issues.
 */
function rangeReducer(state: RangeState, action: RangeAction): RangeState {
	switch (action.type) {
		case "SELECT": {
			const { startMs, endMs } = state;
			const ms = action.ms;

			// Case 1: No start date → set start
			if (startMs === null) return { startMs: ms, endMs: null };

			// Case 2: Both start and end exist → reset, set start = ms
			if (endMs !== null) return { startMs: ms, endMs: null };

			// Case 3: Has start, no end, ms > start → set end
			if (ms > startMs) return { startMs, endMs: ms };

			// Case 4: ms <= start → reset, set start = ms
			return { startMs: ms, endMs: null };
		}
		case "CLEAR":
			return { startMs: null, endMs: null };
		case "RESET":
			return { startMs: action.startMs, endMs: action.endMs };
		default:
			return state;
	}
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

/**
 * Hook for managing state of DateRangePicker.
 *
 * @param options - Initial configuration
 * @returns `DateRangePickerState` object with state and actions
 */
export const useDateRangePickerState = (
	options?: UseDateRangePickerStateOptions,
): DateRangePickerState => {
	// ── Initial values ──────────────────────────────────────────────────────────
	const initialStartMs =
		options?.initialStartMs != null
			? startOfDayUTC(options.initialStartMs)
			: null;
	const initialEndMs =
		options?.initialEndMs != null ? startOfDayUTC(options.initialEndMs) : null;

	const initialMonthMs = normalizeToMonthStart(
		options?.initialDisplayedMonthMs ??
			(initialStartMs !== null ? initialStartMs : todayUTC()),
	);

	const yearRange = options?.yearRange ?? DEFAULT_YEAR_RANGE;
	const selectableDates = options?.selectableDates ?? ALL_DATES_SELECTABLE;
	const initialDisplayMode: DisplayMode =
		options?.initialDisplayMode ?? "picker";
	const locale = resolveLocale(options?.locale);

	// ── State ───────────────────────────────────────────────────────────────────
	const [{ startMs: selectedStartMs, endMs: selectedEndMs }, dispatch] =
		useReducer(rangeReducer, {
			startMs: initialStartMs,
			endMs: initialEndMs,
		});

	const [displayedMonthMs, setDisplayedMonthMs] =
		useState<number>(initialMonthMs);
	const [displayMode, setDisplayModeState] =
		useState<DisplayMode>(initialDisplayMode);

	// ── Actions ─────────────────────────────────────────────────────────────────

	/**
	 * Dispatch atomic SELECT action — start and end are updated simultaneously
	 * in reducer, avoiding nested setState anti-pattern.
	 */
	const selectDate = useCallback(
		(ms: number): void => {
			const normalized = startOfDayUTC(ms);
			if (!selectableDates.isSelectableDate(normalized)) return;
			dispatch({ type: "SELECT", ms: normalized });
		},
		[selectableDates],
	);

	const clearRange = useCallback((): void => {
		dispatch({ type: "CLEAR" });
	}, []);

	const navigateMonthAction = useCallback(
		(direction: "prev" | "next"): void => {
			setDisplayedMonthMs(
				(prev) =>
					getNavigableMonth(prev, direction, yearRange, selectableDates) ??
					prev,
			);
		},
		[yearRange, selectableDates],
	);

	const navigateToMonth = useCallback(
		(year: number, month: number): void => {
			if (!isMonthNavigable(year, yearRange, selectableDates)) return;
			setDisplayedMonthMs(Date.UTC(year, month, 1));
		},
		[yearRange, selectableDates],
	);

	const setDisplayMode = useCallback((mode: DisplayMode): void => {
		setDisplayModeState(mode);
	}, []);

	const reset = useCallback((): void => {
		dispatch({ type: "RESET", startMs: initialStartMs, endMs: initialEndMs });
		setDisplayedMonthMs(initialMonthMs);
		setDisplayModeState(initialDisplayMode);
	}, [initialStartMs, initialEndMs, initialMonthMs, initialDisplayMode]);

	// ── Memoized state object ───────────────────────────────────────────────────
	return useMemo<DateRangePickerState>(
		() => ({
			selectedStartMs,
			selectedEndMs,
			displayedMonthMs,
			displayMode,
			yearRange,
			locale,
			selectableDates,
			selectDate,
			clearRange,
			navigateMonth: navigateMonthAction,
			navigateToMonth,
			setDisplayMode,
			reset,
		}),
		[
			selectedStartMs,
			selectedEndMs,
			displayedMonthMs,
			displayMode,
			yearRange,
			locale,
			selectableDates,
			selectDate,
			clearRange,
			navigateMonthAction,
			navigateToMonth,
			setDisplayMode,
			reset,
		],
	);
};
